import { useState, useEffect } from 'react';
import { organizationsApi } from '../../api/organizations.js';
import { Modal } from '../../components/common/Modal.jsx';
import { FormField, Input, Select, Textarea } from '../../components/common/FormField.jsx';
import { LoadingSpinner } from '../../components/common/LoadingSpinner.jsx';
import { ErrorAlert } from '../../components/common/ErrorAlert.jsx';
import { usePageHeader } from '../../hooks/usePageHeader.jsx';

const ORG_TYPES = [
  { value: 'pmi-chapter',   label: 'PMI Chapter' },
  { value: 'agile-meetup',  label: 'Agile Meetup' },
  { value: 'user-group',    label: 'User Group' },
  { value: 'other',         label: 'Other' },
];

function typeLabel(type) {
  return ORG_TYPES.find((t) => t.value === type)?.label ?? type ?? '—';
}

const EMPTY_FORM = { name: '', type: 'pmi-chapter', description: '' };

export function OrgAdminPage() {
  usePageHeader('Organizations');
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formModal, setFormModal] = useState(false);
  const [editing, setEditing] = useState(null); // org object or null (= create)
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const result = await organizationsApi.list();
      setOrgs(result ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormModal(true);
  };

  const openEdit = (org) => {
    setEditing(org);
    setForm({ name: org.name, type: org.type ?? 'other', description: org.description ?? '' });
    setFormModal(true);
  };

  const closeModal = () => {
    setFormModal(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving('save');
    try {
      const data = {
        name: form.name.trim(),
        type: form.type,
        description: form.description.trim() || null,
      };
      if (editing) {
        await organizationsApi.update(editing.id, data);
      } else {
        await organizationsApi.create(data);
      }
      closeModal();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const remove = async (org) => {
    if (!confirm(`Delete organization "${org.name}"? This cannot be undone.`)) return;
    setSaving(org.id);
    try {
      await organizationsApi.delete(org.id);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <LoadingSpinner label="Loading organizations…" />;

  return (
    <div className="page-container" style={{ maxWidth: 'none' }}>
      <div className="between" style={{ marginBottom: 'var(--space-6)', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={openCreate}>New organization</button>
      </div>

      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      {orgs.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>No organizations yet.</p>
      ) : (
        <div className="data-table" style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Type</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((org) => (
                <tr key={org.id}>
                  <td style={{ fontWeight: 500 }}>{org.name}</td>
                  <td style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>{org.id}</td>
                  <td>
                    <span className="badge badge-neutral">{typeLabel(org.type)}</span>
                  </td>
                  <td style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', maxWidth: 300 }}>
                    {org.description ?? '—'}
                  </td>
                  <td>
                    <div className="inline">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => openEdit(org)}
                        disabled={saving === org.id}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => remove(org)}
                        disabled={saving === org.id}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={formModal}
        onClose={closeModal}
        title={editing ? 'Edit organization' : 'New organization'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal} disabled={saving === 'save'}>
              Cancel
            </button>
            <button className="btn btn-primary" form="org-form" type="submit" disabled={saving === 'save'}>
              {saving === 'save' ? 'Saving…' : editing ? 'Save changes' : 'Create'}
            </button>
          </>
        }
      >
        <form id="org-form" onSubmit={save} className="stack">
          <FormField label="Name" required id="orgName">
            <Input
              id="orgName"
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="PMI Greater Boston Chapter"
              required
            />
          </FormField>
          <FormField label="Type" id="orgType">
            <Select
              id="orgType"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            >
              {ORG_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Select>
          </FormField>
          <FormField label="Description" id="orgDesc">
            <Textarea
              id="orgDesc"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional description"
              rows={3}
            />
          </FormField>
          {!editing && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
              The organization ID will be auto-generated from the name and cannot be changed later.
            </p>
          )}
        </form>
      </Modal>
    </div>
  );
}
